import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { projectService, Project, CreateProjectData } from '../../services/project.service';
import { clientService, Client } from '../../services/client.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);
}

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'in_progress', label: 'En curso' },
  { value: 'planning', label: 'Planeación' },
  { value: 'completed', label: 'Completados' },
];

const INITIAL_FORM: CreateProjectData = {
  name: '',
  description: '',
  client_id: '',
  project_lead_id: '',
  address: '',
  budget: 0,
  status: 'planning',
};

export function ProjectsPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateProjectData>(INITIAL_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const canCreate = ['sys_admin', 'general_manager', 'project_lead'].includes(user?.role ?? '');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [projectsData, clientsData] = await Promise.all([
        projectService.getProjects(),
        clientService.getClients().catch(() => [] as Client[]),
      ]);
      setProjects(projectsData);
      setClients(clientsData);
    } catch (e: any) {
      setError(e.message || 'Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!term) return true;
      return (
        p.name.toLowerCase().includes(term) ||
        (p.description ?? '').toLowerCase().includes(term) ||
        (p.client_name ?? '').toLowerCase().includes(term)
      );
    });
  }, [projects, search, statusFilter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.client_id || formData.budget <= 0) {
      showError('Completa nombre, cliente y presupuesto');
      return;
    }
    setIsSaving(true);
    try {
      await projectService.createProject(formData);
      showSuccess('Proyecto creado');
      setShowForm(false);
      setFormData(INITIAL_FORM);
      await loadData();
    } catch (e: any) {
      showError(e.message || 'Error al crear proyecto');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Proyectos</h1>
          <p className="text-xs text-slate-500">{projects.length} en total</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
          >
            + Nuevo
          </button>
        )}
      </div>

      <div className="relative">
        <input
          type="search"
          placeholder="Buscar proyecto, cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              statusFilter === f.value
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingState message="Cargando proyectos…" />
      ) : error ? (
        <ErrorState message={error} onRetry={loadData} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="🏗️"
          title={projects.length === 0 ? 'Sin proyectos' : 'Sin resultados'}
          description={
            projects.length === 0
              ? 'Crea el primer proyecto para comenzar.'
              : 'Prueba con otro término o filtro.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {filtered.map((p) => (
            <li key={p.id}>
              <Link
                to={`/gestion/proyectos/${p.id}`}
                className="block bg-white rounded-xl shadow-sm p-4 active:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-slate-900 truncate">
                      {p.name}
                    </h3>
                    <p className="text-xs text-slate-500 truncate">
                      {p.client_name ?? p.client_id.slice(0, 8)}
                    </p>
                  </div>
                  <StatusBadge status={p.status} />
                </div>

                {typeof p.progress === 'number' && (
                  <div className="mb-2">
                    <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                      <span>Avance</span>
                      <span className="font-semibold text-slate-800">{p.progress}%</span>
                    </div>
                    <ProgressBar value={p.progress} />
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold text-primary">{formatCurrency(p.budget)}</span>
                  {p.address && (
                    <>
                      <span>·</span>
                      <span className="truncate">{p.address}</span>
                    </>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nuevo proyecto</h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 text-2xl leading-none"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Cliente *
                </label>
                <select
                  required
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="">Selecciona…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Presupuesto (MXN) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={formData.budget || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  value={formData.address ?? ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Estado
                </label>
                <select
                  value={formData.status ?? 'planning'}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as Project['status'] })
                  }
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="planning">Planificación</option>
                  <option value="in_progress">En progreso</option>
                  <option value="on_hold">En pausa</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {isSaving ? 'Guardando…' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}